import { useEffect, useMemo, useState } from "react";
import { api } from "../../../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney, fmtDate } from "../../../lib/format";

interface ClientRow {
  id: number; name: string; email: string | null; language: "en" | "th";
  credit_terms_days: number; open_count: number; open_total: number;
  overdue_count: number; overdue_total: number;
  last_sent_at: string | null;
}

interface SendResult {
  client_id: number;
  client_name: string;
  status: "sent" | "failed" | "skipped";
  provider_id?: string;
  error?: string;
}

interface SendResponse {
  queued?: number;
  would_send?: number;
  dry_run?: boolean;
  skipped?: SendResult[];
}

export default function Send() {
  const { current } = useCurrentCompany();
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [previewing, setPreviewing] = useState<{ html: string; subject: string; to: string | null; lang: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [results, setResults] = useState<SendResult[] | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!current) return;
    api<{ clients: ClientRow[] }>(`/api/collection/clients?company_id=${current.id}`)
      .then((r) => setRows(r.clients.filter((c) => c.open_count > 0)));
  }, [current?.id]);

  const filtered = useMemo(() => {
    const base = overdueOnly ? rows.filter((c) => c.overdue_count > 0) : rows;
    const needle = q.trim().toLowerCase();
    if (!needle) return base;
    return base.filter((c) =>
      c.name.toLowerCase().includes(needle) ||
      (c.email || "").toLowerCase().includes(needle));
  }, [rows, q, overdueOnly]);

  const eligible = useMemo(() => filtered.filter((r) => r.email), [filtered]);
  const allSelected = eligible.length > 0 && eligible.every((r) => selected.has(r.id));
  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(eligible.map((r) => r.id)));
  }
  function toggle(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }

  async function preview(clientId: number) {
    setPreviewLoading(true);
    try {
      const r = await api<{ subject: string; html: string; to: string | null; language: string }>(
        "/api/collection/preview", { method: "POST", body: JSON.stringify({ client_id: clientId, overdue_only: overdueOnly }) },
      );
      setPreviewing({ subject: r.subject, html: r.html, to: r.to, lang: r.language });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Preview failed");
    } finally { setPreviewLoading(false); }
  }

  async function send() {
    if (!selected.size) return;
    if (!dryRun) {
      const companyName = current?.name || "(unknown company)";
      const scope = overdueOnly ? "\n\nReminders include OVERDUE invoices only." : "";
      const msg = `Send ${selected.size} reminder email${selected.size > 1 ? "s" : ""} FROM\n\n  "${companyName}"\n\nto the selected clients?${scope}\n\n(Each will go via finance@email.mailbit.co.th. admin@mailbit.co.th is CC'd.)`;
      if (!confirm(msg)) return;
    }
    setSending(true); setResults(null); setSummary(null);
    try {
      const r = await api<SendResponse>("/api/collection/send", {
        method: "POST",
        body: JSON.stringify({ client_ids: Array.from(selected), dry_run: dryRun, overdue_only: overdueOnly }),
      });
      setResults(r.skipped || []);
      if (dryRun) {
        setSummary(`Dry run: ${r.would_send ?? 0} reminder${(r.would_send ?? 0) === 1 ? "" : "s"} would be sent.`);
      } else {
        const n = r.queued ?? 0;
        setSummary(n > 0
          ? `Queued ${n} reminder${n === 1 ? "" : "s"} — they send in the background and appear in the Log as they go out.`
          : "Nothing was queued.");
      }
      setSelected(new Set());
      if (current) {
        const refreshed = await api<{ clients: ClientRow[] }>(`/api/collection/clients?company_id=${current.id}`);
        setRows(refreshed.clients.filter((c) => c.open_count > 0));
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Send failed");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Send reminders</h1>
        <p className="mt-1 text-sm text-slate-500">
          Acting as <span className="font-semibold text-indigo-700">{current?.name || "—"}</span>
          {" · "}reminders go from <span className="font-mono">finance@email.mailbit.co.th</span>, CC <span className="font-mono">admin@mailbit.co.th</span>
        </p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative w-72">
            <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>
            <input className="search-input w-full !h-9" placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>

          <div className="text-sm text-slate-500">
            <span className="font-medium text-slate-900">{selected.size}</span> of {eligible.length} selected
          </div>

          <label className="flex items-center gap-1.5 text-sm" title="Only include invoices past their due date (issue date + credit terms). Clients with no overdue invoices are hidden.">
            <input type="checkbox" checked={overdueOnly}
                   onChange={(e) => { setOverdueOnly(e.target.checked); setSelected(new Set()); }} />
            Overdue only
          </label>

          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Dry run (no email sent)
          </label>

          <div className="ml-auto flex items-center gap-2">
            <button className="btn-secondary" onClick={toggleAll}>{allSelected ? "Clear" : "Select all"}</button>
            <button className="btn-primary" onClick={send} disabled={!selected.size || sending}>
              {sending ? "Sending…" : (dryRun ? `Dry run → ${selected.size}` : `Send to ${selected.size}`)}
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="refined">
          <colgroup>
            <col style={{ width: "44px" }} />
            <col />
            <col />
            <col style={{ width: "80px" }} />
            <col style={{ width: "90px" }} />
            <col style={{ width: "150px" }} />
            <col style={{ width: "150px" }} />
            <col style={{ width: "110px" }} />
          </colgroup>
          <thead>
            <tr>
              <th className="cell-check"><input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select all" /></th>
              <th>Client</th>
              <th>Email</th>
              <th>Lang</th>
              <th>{overdueOnly ? "Overdue" : "Open"}</th>
              <th className="text-right">{overdueOnly ? "Overdue total" : "Total"}</th>
              <th>Last sent</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const disabled = !c.email;
              const checked = selected.has(c.id);
              return (
                <tr key={c.id} className={checked ? "bg-indigo-50/40" : ""}>
                  <td className="cell-check">
                    <input type="checkbox" disabled={disabled}
                           checked={checked} onChange={() => toggle(c.id)} />
                  </td>
                  <td className="font-medium text-slate-900">{c.name}</td>
                  <td>{c.email
                    ? <span className="text-slate-700">{c.email}</span>
                    : <span className="text-xs text-rose-600">missing — add on Clients tab</span>}</td>
                  <td><span className="pill pill-slate uppercase">{c.language}</span></td>
                  <td className="text-slate-700 tabular-nums">{overdueOnly ? c.overdue_count : c.open_count}</td>
                  <td className="text-right font-mono font-semibold text-slate-900">{fmtMoney(overdueOnly ? c.overdue_total : c.open_total, current?.currency || "THB")}</td>
                  <td className="text-xs text-slate-500">{c.last_sent_at ? fmtDate(c.last_sent_at) : "—"}</td>
                  <td className="text-right">
                    <button className="btn-secondary !py-1 !px-2.5 text-xs" disabled={disabled || previewLoading}
                            onClick={() => preview(c.id)}>Preview</button>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-500">
                {rows.length ? "No matches for your search." : "No clients with open invoices."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {previewing && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
             onClick={() => setPreviewing(null)}>
          <div className="card max-h-[90vh] w-full max-w-2xl overflow-y-auto p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="text-xs uppercase text-slate-500">To · {previewing.lang.toUpperCase()}</div>
            <div className="font-mono text-sm">{previewing.to}</div>
            <div className="mt-3 text-xs uppercase text-slate-500">Subject</div>
            <div className="font-semibold">{previewing.subject}</div>
            <div className="mt-3 rounded-md border border-slate-200 p-4"
                 dangerouslySetInnerHTML={{ __html: previewing.html }} />
            <div className="mt-4 text-right">
              <button className="btn-secondary" onClick={() => setPreviewing(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {(summary || (results && results.length > 0)) && (
        <div className="card p-4">
          <div className="font-semibold">Result</div>
          {summary && (
            <div className="mt-2 rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{summary}</div>
          )}
          {results && results.length > 0 && (
            <ul className="mt-2 space-y-1 text-sm">
              {results.map((r) => (
                <li key={r.client_id} className="flex items-center gap-2">
                  <span className="pill pill-amber">{r.status}</span>
                  <span>{r.client_name}</span>
                  {r.error && <span className="text-rose-700">— {r.error}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
